(() => {
  const stylesUrl = document.currentScript?.dataset.rewardsStylesUrl;
  if (!stylesUrl) return;

  const selector = '.gw-rd-page-wrapper [class*="gw-rd-"][class*="-placeholder"]';
  const marker = 'data-rewards-page-styles';
  const bannerSectionSuffix = '__image_banner_qd4taK';
  const layoutObserver = new MutationObserver(groupContent);

  function moveSection(parent, section, before = null) {
    // Preserve widget state when the browser supports state-preserving moves.
    if (typeof parent.moveBefore === 'function') parent.moveBefore(section, before);
    else parent.insertBefore(section, before);
  }

  function groupContent() {
    layoutObserver.disconnect();
    document.querySelectorAll('.gw-rd-page-wrapper').forEach(page => {
      let content = page.querySelector(':scope > .rewards-page-content');
      const sections = [...page.children].flatMap(child =>
        child === content ? [...content.children] : [child]
      ).filter(child => child.classList.contains('shopify-section'));
      const banner = sections.find(section => section.id.endsWith(bannerSectionSuffix));
      if (!banner) {
        layoutObserver.observe(page, { childList: true });
        return;
      }

      if (!content) {
        content = document.createElement('div');
        content.className = 'rewards-page-content';
        banner.after(content);
      }
      if (banner.parentElement !== page) moveSection(page, banner, content);
      const modules = sections.filter(section => section !== banner);
      modules.forEach((section, index) => {
        if (content.children[index] !== section) {
          moveSection(content, section, content.children[index] || null);
        }
      });
      layoutObserver.observe(page, { childList: true });
      layoutObserver.observe(content, { childList: true });
    });
  }

  function injectStyles() {
    document.querySelectorAll(selector).forEach(host => {
      if (host.classList.contains('gw-rd-referral-program-page-widget-placeholder')) {
        const block = host.closest('.custom-app-block');
        if (block && !block.classList.contains('rewards-referral')) {
          block.classList.add('rewards-referral');
        }
      }
      if (host.classList.contains('gw-rd-vip-tiers-page-widget-placeholder')) {
        const block = host.closest('.custom-app-block');
        if (block && !block.classList.contains('rewards-vip-tiers')) {
          block.classList.add('rewards-vip-tiers');
        }
      }
      const root = host.shadowRoot;
      if (!root) return;
      injectStylesIntoRoot(root);

      // VIP progress has its own shadow boundary inside the page widget.
      // Check even when the outer stylesheet is already present: this nested
      // component may mount later or be replaced by Growave.
      if (host.classList.contains('gw-rd-vip-tiers-page-widget-placeholder')) {
        root.querySelectorAll('.gw-rd-vip-tiers-single-progress-widget-container').forEach(progress => {
          if (progress.shadowRoot) {
            injectStylesIntoRoot(progress.shadowRoot);
            syncCurrentVipTier(progress.shadowRoot);
          }
        });
      }
    });
    syncVipBalance();
    syncEmptyAppBlocks();
  }

  function syncVipBalance() {
    const sources = [...document.querySelectorAll('.gw-rd-page-wrapper .gw-rd-ways-to-earn-page-widget-placeholder')]
      .map(host => host.shadowRoot?.querySelector('.gw-rd-page-widget-customer-balance')).filter(Boolean);
    const source = sources.find(balance => balance.querySelector('h2')?.textContent.trim());
    const targets = [...document.querySelectorAll('.gw-rd-page-wrapper .gw-rd-vip-tiers-page-widget-placeholder')]
      .map(host => host.shadowRoot?.querySelector('.gw-rd-vip-tiers-page-widget-tier-progress__status')).filter(Boolean);
    const balanceText = source?.querySelector('h2')?.textContent.replace(/\s+/g, ' ').trim();
    const expirationText = source?.querySelector('.gw-rd-page-widget-customer-balance__points-expiration')
      ?.textContent.replace(/\s+/g, ' ').trim() || '';
    let relocated = false;

    targets.forEach(target => {
      let panel = target.querySelector(':scope > .rewards-vip-balance');
      if (!balanceText) {
        panel?.remove();
        target.removeAttribute('data-rewards-vip-balance');
        return;
      }
      const signature = JSON.stringify([balanceText, expirationText]);
      if (!panel) {
        panel = document.createElement('div');
        panel.className = 'rewards-vip-balance';
        target.append(panel);
      }
      if (panel.dataset.balanceSignature !== signature) {
        const row = document.createElement('div');
        row.className = 'rewards-vip-balance__row';
        // Split the native text only for styling; points and dates stay exactly
        // as Growave renders them, including zero balances and localization.
        const parts = balanceText.match(/^(.*?)(\d[\d.,\s]*\s+\S.*)$/);
        if (parts) {
          const label = document.createElement('span');
          label.textContent = parts[1].trim();
          const points = document.createElement('strong');
          points.className = 'rewards-vip-balance__points';
          points.textContent = parts[2];
          row.append(label, points);
        } else {
          row.textContent = balanceText;
        }
        panel.replaceChildren(row);
        if (expirationText) {
          const expiration = document.createElement('div');
          expiration.className = 'rewards-vip-balance__row rewards-vip-balance__expiration';
          const parts = expirationText.match(/^([^:]+):\s*(.+)$/);
          if (parts) {
            const label = document.createElement('span');
            label.textContent = `${parts[1]}:`;
            const date = document.createElement('strong');
            date.textContent = parts[2];
            expiration.append(label, date);
          } else {
            expiration.textContent = expirationText;
          }
          panel.append(expiration);
        }
        panel.dataset.balanceSignature = signature;
      }
      target.setAttribute('data-rewards-vip-balance', '');
      relocated = true;
    });
    sources.forEach(balance => balance.toggleAttribute('data-rewards-balance-relocated', relocated));
  }

  function syncEmptyAppBlocks() {
    const sections = new Set();
    document.querySelectorAll('.gw-rd-page-wrapper .custom-app-block').forEach(block => {
      // Measure the rendered app, not this wrapper's own margins. Do not hide
      // the wrapper: Growave must be able to mount and become visible later.
      const hasContent = [...block.children].some(child => child.getBoundingClientRect().height > 0);
      block.toggleAttribute('data-rewards-empty', !hasContent);
      const section = block.closest('.shopify-section');
      if (section) sections.add(section);
    });
    sections.forEach(section => {
      const blocks = [...section.querySelectorAll('.custom-app-block')];
      section.toggleAttribute('data-rewards-empty-section', blocks.length > 0 &&
        blocks.every(block => block.hasAttribute('data-rewards-empty')));
    });
  }

  function syncCurrentVipTier(root) {
    const widget = root.querySelector('.gw-rd-vip-tiers-single-progress-widget');
    if (!widget) return;
    // Use Growave's current (left-hand) tier label, never a hard-coded level.
    const current = widget.querySelector('.gw-rd-vip-tiers-single-progress-widget__tier-titles > .gw-layout-text:first-child');
    const tier = current?.textContent.trim();
    let heading = widget.querySelector(':scope > .rewards-vip-current-tier');
    if (!tier) {
      heading?.remove();
      return;
    }
    if (!heading) {
      heading = document.createElement('div');
      heading.className = 'rewards-vip-current-tier';
      heading.append(document.createTextNode('Your VIP Tires '), document.createElement('strong'));
      widget.prepend(heading);
    }
    const value = heading.querySelector('strong');
    if (value.textContent !== tier) value.textContent = tier;
  }

  function injectStylesIntoRoot(root) {
    if (root.querySelector(`link[${marker}]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = stylesUrl;
    link.setAttribute(marker, '');
    root.append(link);
  }

  // A shadow root can be attached after its host enters the document; that
  // attachment does not trigger a document MutationObserver. Keep checking
  // while this page is active, including widgets reloaded by the theme editor.
  let timer;
  function start() {
    groupContent();
    injectStyles();
    if (!timer) timer = window.setInterval(injectStyles, 1000);
  }

  window.addEventListener('pagehide', () => {
    window.clearInterval(timer);
    timer = null;
    layoutObserver.disconnect();
  });
  window.addEventListener('pageshow', start);
  document.addEventListener('gwSdkReady', injectStyles);
  document.addEventListener('shopify:section:load', injectStyles);
  document.addEventListener('shopify:section:load', groupContent);
  document.addEventListener('shopify:section:reorder', groupContent);
  start();
})();
